import { MessageSquare, Settings2, Users, Workflow } from "lucide-react";
import { useAppStore } from "../lib/store";
import type { EngineId } from "../lib/types";

interface EngineSelectorProps {
  onConfigureCouncil: () => void;
  onConfigureAgentTeam: () => void;
}

/**
 * Segmented control over the three execution engines.
 *
 * This replaces two icon buttons that only ever *opened* a config modal: once
 * Council or Agent Team was on, nothing in the main UI turned it back off, and
 * the modal's "Disattiva" left the dialog open so it read as a no-op. Making
 * the engines mutually exclusive segments means "back to plain chat" is always
 * one click, and the active engine is always legible.
 */
export function EngineSelector({
  onConfigureCouncil,
  onConfigureAgentTeam,
}: EngineSelectorProps) {
  const { settings, setCouncil, setAgentTeam } = useAppStore();
  const active: EngineId = settings.agentTeam.enabled
    ? "agent_team"
    : settings.council.enabled
      ? "council"
      : "standard";

  // `every` on an empty list is true, so the length check is load-bearing.
  const councilReady =
    settings.council.models.length > 0 &&
    settings.council.models.every((item) => item.model) &&
    !!settings.council.synthesis.model;
  const teamReady =
    settings.agentTeam.agents.length > 0 &&
    settings.agentTeam.agents.every((agent) => agent.model) &&
    !!settings.agentTeam.orchestrator.model &&
    !!settings.agentTeam.synthesizer.model;

  const selectStandard = () => {
    setCouncil({ enabled: false });
    setAgentTeam({ enabled: false });
  };
  const selectCouncil = () => {
    if (!councilReady) {
      onConfigureCouncil();
      return;
    }
    setAgentTeam({ enabled: false });
    setCouncil({ enabled: true });
  };
  const selectTeam = () => {
    if (!teamReady) {
      onConfigureAgentTeam();
      return;
    }
    setCouncil({ enabled: false });
    setAgentTeam({ enabled: true });
  };

  return (
    <div className="engine-selector" role="radiogroup" aria-label="Motore di esecuzione">
      <button
        role="radio"
        aria-checked={active === "standard"}
        className={active === "standard" ? "engine-option active" : "engine-option"}
        onClick={selectStandard}
        title="Chat con un solo modello"
      >
        <MessageSquare size={14} />
        <span>Chat</span>
      </button>

      <div className={active === "council" ? "engine-option active" : "engine-option"}>
        <button
          role="radio"
          aria-checked={active === "council"}
          onClick={selectCouncil}
          title={
            councilReady
              ? "Più modelli discutono, un sintetizzatore decide"
              : "Configura il Council per attivarlo"
          }
        >
          <Users size={14} />
          <span>Council</span>
        </button>
        <button
          className="engine-config"
          onClick={onConfigureCouncil}
          aria-label="Configura Council"
          title="Configura Council"
        >
          <Settings2 size={12} />
        </button>
      </div>

      <div className={active === "agent_team" ? "engine-option active" : "engine-option"}>
        <button
          role="radio"
          aria-checked={active === "agent_team"}
          onClick={selectTeam}
          title={
            teamReady
              ? "Agenti specializzati coordinati da un orchestratore"
              : "Configura l'Agent Team per attivarlo"
          }
        >
          <Workflow size={14} />
          <span>Team</span>
        </button>
        <button
          className="engine-config"
          onClick={onConfigureAgentTeam}
          aria-label="Configura Agent Team"
          title="Configura Agent Team"
        >
          <Settings2 size={12} />
        </button>
      </div>
    </div>
  );
}
